import React, { useRef } from 'react'
import {
    Box,
    Center,
    Flex,
    Icon,
    IconButton,
    Spinner,
} from '@chakra-ui/react'
import { LuChevronUp, LuGrip, LuX } from 'react-icons/lu'
import { FreeFocusInside } from 'react-focus-lock'

import { useInternalChatQuery } from '~graphql/generated/graphql'
import { useOpenTeamChats } from './open-team-chats-provider'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import { LocalStorageKeys } from '~config/constants'
import { ASK_CHCKVET_WIDTH_CSS_VAR } from '~components/ask-chckvet/ask-chckvet-context'
import TeamChatConversationPanel from '~components/team-chat/conversation/panel'
import { InternalChatTitle } from '~components/team-chat/layout/sidebar/chat-item'
import { useUserContext } from '~config/user-context'

const POPOVER_WIDTH = 380
const POPOVER_HEIGHT = 540
const MIN_POPOVER_WIDTH = 320
const MAX_POPOVER_WIDTH = 720
const MIN_POPOVER_HEIGHT = 360
const MAX_POPOVER_HEIGHT = 840
const MINIMIZED_HEIGHT = 44
const RIGHT_OFFSET = 16
const STACK_GAP = 12

type TeamChatPopoverSize = {
    width: number
    height: number
}

interface Props {
    chatId: string
    /** Index in the open-chats stack. 0 = rightmost among team chats. */
    stackIndex: number
}

export default function TeamChatPopover({ chatId, stackIndex }: Props) {
    const {
        closeTeamChat,
        toggleMinimizeTeamChat,
        isTeamChatMinimized,
    } = useOpenTeamChats()
    const { user } = useUserContext()
    const minimized = isTeamChatMinimized(chatId)
    const [popoverSize, setPopoverSize] = useLocalStorage<TeamChatPopoverSize>(
        LocalStorageKeys.teamChatPopoverSize,
        {
            width: POPOVER_WIDTH,
            height: POPOVER_HEIGHT,
        },
    )
    const resizeStartRef = useRef<{
        pointerX: number
        pointerY: number
        width: number
        height: number
    }>()

    const { data, loading } = useInternalChatQuery({
        variables: { id: chatId },
        skip: !chatId,
    })

    const chat = data?.internalChat
    const displayUsers = (chat?.internal_chat_users || []).filter(
        member => member.user.id !== user?.id && !member.is_removed,
    )

    const width = clamp(popoverSize.width, MIN_POPOVER_WIDTH, MAX_POPOVER_WIDTH)
    const expandedHeight = clamp(
        popoverSize.height,
        MIN_POPOVER_HEIGHT,
        MAX_POPOVER_HEIGHT,
    )
    const right = `calc(${
        RIGHT_OFFSET + stackIndex * (width + STACK_GAP)
    }px + var(${ASK_CHCKVET_WIDTH_CSS_VAR}, 0px))`
    const height = minimized ? MINIMIZED_HEIGHT : expandedHeight

    function handleResizeStart(event: React.PointerEvent<HTMLDivElement>) {
        if (minimized) return

        event.preventDefault()
        event.stopPropagation()

        resizeStartRef.current = {
            pointerX: event.clientX,
            pointerY: event.clientY,
            width,
            height: expandedHeight,
        }
        event.currentTarget.setPointerCapture(event.pointerId)
    }

    function handleResizeMove(event: React.PointerEvent<HTMLDivElement>) {
        const start = resizeStartRef.current
        if (!start) return

        event.preventDefault()
        event.stopPropagation()

        setPopoverSize({
            width: clamp(
                start.width + start.pointerX - event.clientX,
                MIN_POPOVER_WIDTH,
                MAX_POPOVER_WIDTH,
            ),
            height: clamp(
                start.height + start.pointerY - event.clientY,
                MIN_POPOVER_HEIGHT,
                MAX_POPOVER_HEIGHT,
            ),
        })
    }

    function handleResizeEnd(event: React.PointerEvent<HTMLDivElement>) {
        if (!resizeStartRef.current) return
        resizeStartRef.current = undefined
        event.currentTarget.releasePointerCapture(event.pointerId)
    }

    return (
        <FreeFocusInside>
            <Box
                position="fixed"
                right={right}
                bottom="0"
                w={`${width}px`}
                h={`${height}px`}
                zIndex={1500}
                bg="white"
                borderTopRadius="lg"
                shadow="2xl"
                borderWidth={1}
                borderBottomWidth={0}
                borderColor="gray.200"
                overflow="hidden"
                display="flex"
                flexDirection="column"
                transition={
                    resizeStartRef.current
                        ? undefined
                        : 'height 0.15s ease-out, width 0.15s ease-out'
                }
            >
                {!minimized && (
                    <ResizeHandle
                        onPointerDown={handleResizeStart}
                        onPointerMove={handleResizeMove}
                        onPointerUp={handleResizeEnd}
                        onPointerCancel={handleResizeEnd}
                    />
                )}
                {minimized ? (
                    loading || !chat ? (
                        <Center flex={1}>
                            <Spinner color="brand.500" />
                        </Center>
                    ) : (
                        <MinimizedHeader
                            title={
                                <InternalChatTitle
                                    chat={chat}
                                    users={displayUsers}
                                    labelOverrides={{
                                        fontSize: 'sm',
                                        fontWeight: 'semibold',
                                        mb: 0,
                                    }}
                                />
                            }
                            onExpand={() => toggleMinimizeTeamChat(chatId)}
                            onClose={() => closeTeamChat(chatId)}
                        />
                    )
                ) : (
                    <TeamChatConversationPanel
                        chatId={chatId}
                        compact
                        onClose={() => closeTeamChat(chatId)}
                        onMinimize={() => toggleMinimizeTeamChat(chatId)}
                    />
                )}
            </Box>
        </FreeFocusInside>
    )
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max)
}

function ResizeHandle({
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
}: {
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void
    onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => void
    onPointerCancel: (event: React.PointerEvent<HTMLDivElement>) => void
}) {
    return (
        <Center
            position="absolute"
            top={1}
            left={1}
            zIndex={2}
            boxSize={6}
            color="gray.400"
            cursor="nwse-resize"
            rounded="md"
            _hover={{
                color: 'gray.600',
                bg: 'gray.50',
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            aria-label="Resize chat"
            role="button"
            title="Drag to resize chat"
        >
            <Icon as={LuGrip} boxSize={4} transform="rotate(45deg)" />
        </Center>
    )
}

function MinimizedHeader({
    title,
    onExpand,
    onClose,
}: {
    title: React.ReactNode
    onExpand: () => void
    onClose: () => void
}) {
    return (
        <Flex
            px={3}
            h="full"
            align="center"
            gap={2}
            bg="white"
            cursor="pointer"
            onClick={onExpand}
            _hover={{ bg: 'gray.50' }}
        >
            <Box flex={1} minW={0}>
                {title}
            </Box>
            <IconButton
                aria-label="Expand chat"
                icon={<Icon as={LuChevronUp} fontSize="md" />}
                size="xs"
                variant="ghost"
                rounded="full"
                onClick={e => {
                    e.stopPropagation()
                    onExpand()
                }}
            />
            <IconButton
                aria-label="Close chat"
                icon={<Icon as={LuX} fontSize="md" />}
                size="xs"
                variant="ghost"
                rounded="full"
                onClick={e => {
                    e.stopPropagation()
                    onClose()
                }}
            />
        </Flex>
    )
}
